import { Component, HostListener, Input, Output, EventEmitter, ElementRef } from "@angular/core";
import { SharedModule } from "@shared/shared.module";
import { TranslateService } from "@ngx-translate/core";
import { UserResponse } from "@interfaces/user";
import { map } from "rxjs/operators";
import { UserState } from "@store/user/user.state";
import { Select, Store } from "@ngxs/store";
import { GET_ACTION_URL, GET_SWITCH_SITE } from "@store/sat-motor/sat-motor.action";
import { BACK_TO_ALPHA, SIGN_OUT } from "src/app/store/user/user.action";
import { NotificationSidebarComponent } from "../notification-sidebar/notification-sidebar.component";
import { NotificationState } from "@store/notification/notification.state";
import { BreakpointObserver } from "@angular/cdk/layout";
import { environment } from "src/environments/environment";

type PersistedSatSession = {
  user?: Partial<UserResponse> | null;
};

@Component({
  selector: "app-motor-header",
  standalone: true,
  imports: [SharedModule, NotificationSidebarComponent],
  templateUrl: "./motor-header.component.html",
  styleUrl: "./motor-header.component.scss",
})
export class MotorHeaderComponent {
  private static readonly SAT_SESSION_STORAGE_KEY = "sat.auth.session.v1";

  @Select(UserState.menu) menuPolicyMgmt$;
  @Select(UserState.menuList) menuList$;
  @Output() isShowMenu: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Select(NotificationState.countUnreadNotification) unreadNotification$;

  @Input() user: UserResponse;

  menuPolicyMgmt: any[] = [];
  menuList: any[] = [];

  userInfo: any;
  private lastLoginDateFromStorage: string | null = null;

  showmenu = true;
  screenWidth: number;
  isMobileScreen: boolean;
  showParentMenu: boolean;

  constructor(
    private readonly translate: TranslateService,
    private readonly store: Store,
    private readonly elementRef: ElementRef,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
    this.screenWidth = window.innerWidth;


  }

  ngOnInit() {
    this.showParentMenu = false;
    this.lastLoginDateFromStorage = this.readSatLastLoginDate();

    this.userInfo = this.store.selectSnapshot<UserResponse>((state) => state.UserState.user);

    this.menuPolicyMgmt$.subscribe((mn: any) => {
      if (mn) this.menuPolicyMgmt = JSON.parse(JSON.stringify(mn.subMenu));
    });

    this.menuList$.subscribe((mn: any) => {
      if (mn) this.menuList = JSON.parse(JSON.stringify(mn));
    });

    this.breakpointObserver.observe(["(max-width: 993px)"]).subscribe((result) => {
      if (result.matches) {
        this.isMobileScreen = true;
        this.showmenu = false;
      } else {
        this.isMobileScreen = false;
        this.showmenu = true;
      }
    });
  }

  // @HostListener("window:resize", ["$event"])
  // onResize(event: Event) {
  //   this.screenWidth = window.innerWidth;
  //   this.showmenu = this.screenWidth >= 993;
  // }

  checkScreen() {
    let screenWidth = window.innerWidth;
    if (screenWidth < 993) {
      this.showmenu = false;
    }
  }

  toggleMenu() {
    this.showmenu = !this.showmenu;
  }

  onHoverToggleParentMenu(isOpen: boolean) {
    this.showParentMenu = isOpen;
    this.isNotificationClicked = false;
  }

  onHoverToggleNonPolicy(isOpen: boolean) {
    this.showParentMenu = isOpen;
  }

  toggleParentMenu() {
    this.showParentMenu = !this.showParentMenu;
    this.isNotificationClicked = false;
  }

  onClick(item: any) {
    item.hidden = !item.hidden;
  }

  nav(url: any, item: any) {
    console.log(`${url} ${JSON.stringify(item)}`);
    item.expanded = !item.expanded;
    this.checkScreen();
    if (url) {
      if (item.moduleOwner === "SAT") {
        if (url.toLowerCase().includes("http") || url.toLowerCase().includes("https")) {
          const _url = this.rewriteSatAbsoluteMenuUrl(url);
          this.store.dispatch(new GET_ACTION_URL(_url));
        } else {
          let _url = item.type === "M" ? this.getSATRedirect(item.menuId) : url + "_SYS_URL";
          this.switchURL(_url);
        }
      } else if (item.moduleOwner === "SAT_MT") {
        if (url.toLowerCase().includes("http") || url.toLowerCase().includes("https")) {
          window.open(url, "", "fullscreen=yes");
        } else {
          this.switchURL(url + "_SYS_URL");
        }
      }
    }
  }

  open(url: any, submenu: any, menu: any) {
    this.checkScreen();
    submenu.expanded = !submenu.expanded;
    menu.expanded = !menu.expanded;
    window.open(url, "", "fullscreen=yes");
  }

  openURL(url, name, spec) {
    window.open(url, name, spec);
  }

  // SAT menu responses can return absolute Alpha switch URLs, including
  // env-prefixed service names such as "uatproj-alpha-sat-svc". Rewrite only
  // that switch path to the sat-motor BFF endpoint before requesting redirectUrl.
  private rewriteSatAbsoluteMenuUrl(url: string): string {
    return url
      .replace(new URL(url).origin, environment.apiUrl)
      .replace(
        /(?:[a-z0-9-]+-)?alpha-sat-svc\/session\/switch\/alpha-service/i,
        "session/sat-motor",
      );
  }

  switchURL(url: any) {
    let queryParams = this.store.selectSnapshot<any>((state) => state.UserState.queryParams);
    this.store.dispatch(new GET_SWITCH_SITE(url)).subscribe((state) => {
      console.log(state.SatMotorState.data.switchSiteUrl);
      if (state.SatMotorState.data.switchSiteUrl) {
        let switchURL = state.SatMotorState.data.switchSiteUrl;
        if (url == "SAT_MT_ENDT_SYS_URL" && queryParams.opportunityId) {
          if (!switchURL.includes("?")) {
            switchURL += queryParams.opportunityId ? `?opportunityId=${queryParams.opportunityId}` : "";
          } else {
            switchURL += queryParams.opportunityId ? `&opportunityId=${queryParams.opportunityId}` : "";
          }
        } else if (url == "SAT_MT_COVER_NOTE_SYS_URL" && queryParams.opportunityId) {
          if (!switchURL.includes("?")) {
            switchURL += queryParams.opportunityId ? `?opportunityId=${queryParams.opportunityId}` : "";
          } else {
            switchURL += queryParams.opportunityId ? `&opportunityId=${queryParams.opportunityId}` : "";
          }
          switchURL += `&ccManualClickSatMenu=true`;
        }
        window.location.href = switchURL;
      }
    });
  }

  switchToAlpha() {
    this.store.dispatch(new BACK_TO_ALPHA());
  }

  signOut() {
    this.store.dispatch(new SIGN_OUT());
  }

  getSATRedirect(id: any) {
    return {
      MNDASHBRD: "SAT_SYS_URL",
      MNELIBRARY: "SAT_ELIBRARY_URL",
      MNSALESMNGT: "SAT_SALES_MANAGEMENT_URL",
      MNCUSTMNGT: "SAT_CUSTOMER_MANAGEMENT_URL",
    }[id];
  }

  isUnifiedMenu(menuId: any) {
    return ["SA_POLSUMMARY", "SA_POLREPORTS", "SA_POLENQUIRY", "SA_POLCI"].includes(menuId);
  }

  isMotorMenu(menuId: any) {
    return ["SAT_MT_MOTOR"].includes(menuId);
  }

  isNonMotorMenu(menuId: any) {
    return ["SA_NONMOTOR"].includes(menuId);
  }

  isNotificationClicked = false;
  unreadNotification = 0;

  toggleNotification(event: any) {
    event.stopPropagation();
    if (this.isNotificationClicked) {
      this.isNotificationClicked = false;
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
    } else {
      this.isNotificationClicked = true;
      if (window.innerWidth < 720) {
      }
    }
    this.showParentMenu = false;
  }

  toggledNotification($event: any) {
    this.isNotificationClicked = !this.isNotificationClicked;
  }

  redirectToDashboard() {
    this.store.dispatch(new GET_SWITCH_SITE("SAT_SYS_URL")).subscribe((state) => {
      window.location.href = state.SatMotorState.data.switchSiteUrl;
    });
  }

  redirectToSummary() {
    this.store.dispatch(new GET_SWITCH_SITE("SUMMARY_SYS_URL")).subscribe((state) => {
      window.location.href = state.SatMotorState.data.switchSiteUrl;
    });
  }

  get userName$() {
    return this.translate.get("HEADER.WELCOME").pipe(map((text: string) => `${text}, ${this.user.preferName}`));
  }

  get lastLogin$() {
    const lastLoginDate = this.lastLoginDateFromStorage ?? this.user?.lastLoginDate ?? "";

    return this.translate.get("HEADER.LAST_LOGIN_TIME").pipe(map((text: string) => `${text}: ${lastLoginDate}`));
  }

  get isAdmin$() {
    if (this.userInfo?.menuAccess) {
      if (this.userInfo.menuAccess.includes("SAT_ADMIN_ALERT")) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  private readSatLastLoginDate(): string | null {
    if (typeof localStorage === "undefined") {
      return null;
    }

    try {
      const rawSession = localStorage.getItem(MotorHeaderComponent.SAT_SESSION_STORAGE_KEY);
      if (!rawSession) {
        return null;
      }

      const parsedSession = JSON.parse(rawSession) as PersistedSatSession;
      const lastLoginDate = parsedSession?.user?.lastLoginDate;

      return typeof lastLoginDate === "string" && lastLoginDate.trim() ? lastLoginDate : null;
    } catch {
      return null;
    }
  }
}
